/**
 * attach event handler to window at `window.remark`
 *
 * made to be run as a standalone script, it can be injected by just about
 * any remark code.  Events won't start flowing until it is connected, so the
 * sooner the better.
 */

type Listener = (...args: any[]) => void;
type Event = string;
type AddListener = (event: Event, listener: Listener) => void;
type Emit = (event: Event, ...args: any[]) => void;
type RemarkEventer = Emit & { on: AddListener; off: AddListener; offAll: () => void; connect: () => void };
export type RemarkWindow = Window &
  typeof globalThis & {
    remark: undefined | { [event: string]: Listener | Listener[] } | RemarkEventer;
  };
declare let window: RemarkWindow;

export const remarkEventerAttached = (x: RemarkWindow['remark']): x is RemarkEventer =>
  typeof x === 'function';
/**
 * IMPORTANT: any changes here should be mirroring in chat-widget/templates/eventer.js until a build step is put in place
 * @returns
 */
export const addRemarkEventer = () => {
  // any handlers that were added before the script ran
  // they should follow the {[event: string]: listener} pattern
  // or {[event: string]: [listenerOne, listenerTwo]} if there is more than one listener
  if (remarkEventerAttached(window.remark)) {
    // this has already been run, don't run it again
    return;
  }
  const listeners = window.remark ?? {};
  const eventQueue: Parameters<Emit>[] = [];
  let connected = false;

  const emit = (event: Event, ...args: any[]) => {
    const eventListeners = listeners[event];
    if (Array.isArray(eventListeners)) {
      eventListeners.forEach((l) => l(...args));
    } else if (typeof eventListeners === 'function') {
      eventListeners(...args);
    }
  };
  const addListener = (event: Event, listener: Listener) => {
    if (Array.isArray(listeners[event])) {
      (listeners[event] as Listener[]).push(listener);
    } else if (event in listeners) {
      listeners[event] = [listeners[event] as Listener, listener];
    } else {
      listeners[event] = listener;
    }
  };
  const removeListener = (event: Event, listener: Listener) => {
    const events = listeners[event];
    if (!events) {
      // if that event key doesn't exist, do nothing
      return;
    }
    if (Array.isArray(events)) {
      // if that event key holds an array, splice it from the list
      const idx = events.findIndex((l) => l == listener);
      (listeners[event] as Listener[]).splice(idx, 1);
    } else {
      // if that event key exists, and the only listener is the passed
      // listener, delete the whole key
      if (events == listener) delete listeners[event];
    }
  };
  const removeAll = () => {
    // delete every key in listeners
    Object.keys((lKey: string) => {
      delete listeners[lKey];
    });
  };
  const drain = () => {
    let eventArgs;
    while ((eventArgs = eventQueue.shift())) {
      emit(...eventArgs);
    }
  };

  const emitter = (...args: Parameters<Emit>) => {
    if (!connected) {
      eventQueue.push(args);
    } else {
      emit(...args);
    }
  };
  const connect = () => {
    eventQueue.unshift(['connect']);
    drain();
    connected = true;
  };
  // attach the event listener to the window
  // const eventEmitter = new EventEmitter();
  // window.remark = Object.assign(eventEmitter.emit, { on: eventEmitter.addListener });
  window.remark = Object.assign(emitter, {
    on: addListener,
    off: removeListener,
    offAll: removeAll,
    connect
  });
};

// -------

/**
 * below here these functions are made to be called
 * by a module that can tie into an npm module.
 * They greatly simplify adding/removing a remark event listener
 * if you aren't sure when the eventer might connect to the page
 */

export const waitForRemarkEventer = (): Promise<void> => {
  let timer: NodeJS.Timeout | undefined;
  return new Promise<void>((res, rej) => {
    const checkForEventer = () => typeof window.remark === 'function' && res();
    checkForEventer();
    timer = setInterval(checkForEventer, 200);
  }).then(() => {
    timer && clearInterval(timer);
  });
};

/**
 * adds a listener for the remark eventer
 * @param event the event to attach to
 * @param listener the listener to attach to an event
 */
export const addRemarkListener: AddListener = (event, listener) => {
  // if it's already been attached, call the listener
  if (typeof window.remark === 'function') {
    (window.remark as any).on(event, listener);
    // if it hasn't been attached, make or merge the pre-scripted object
  } else if (typeof window.remark !== 'object') {
    // we've already checked if it's a function.
    // so if we're here it isn't a function.
    // and if it isn't an object either, create it as an object
    window.remark = { [event]: listener };
  } else if (!(event in window.remark)) {
    // if it's already an object, but the key doesn't exist yet,
    // set the key to the listener
    window.remark[event] = listener;
  } else if (event in window.remark) {
    // if it's already an object, and the key already exists,
    // decide how to merge it in
    const otherEvents = window.remark[event];
    // if the events is already an array, concat
    if (Array.isArray(otherEvents)) {
      window.remark[event] = otherEvents.concat(listener);
    } else {
      // if this is the second listener, create it as an array
      window.remark[event] = [otherEvents, listener];
    }
  }
};

/**
 * removes a listener from the remark eventer
 * checks listener by reference against the list of listener functions
 * @param event the event to detach from
 * @param listener the listener to detach from an event.
 */
export const removeRemarkListener: AddListener = (event, listener) => {
  if (typeof window.remark === 'function') {
    (window.remark as any).off(event, listener);
  } else if (typeof window.remark !== 'object' || !(event in window.remark)) {
    // if window.remark isn't an object or the event key doesn't exist,
    // there's nothing more to do
    return;
  } else if (event in window.remark) {
    // window.remark is an object,

    if (Array.isArray(window.remark[event])) {
      // if that event key holds an array, splice it from the list
      const idx = (window.remark[event] as Listener[])?.findIndex((l) => l == listener);
      (window.remark[event] as Listener[]).splice(idx, 1);
    } else {
      // if that event key exists, and the only listener is the passed
      // listener, delete the whole key
      if (window.remark[event] == listener) delete window.remark[event];
    }
  }
};
